/**
 * StationSlidesConfig
 *
 * Choose which stations to include and arrange their
 * order. Also exposes the table to set timers and languages.
 */
import React from "react";
import {
  Card,
  CardContent,
  Stack,
  Typography,
  Chip,
  Paper,
  IconButton,
  Tooltip,
  Box,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CloseIcon from "@mui/icons-material/Close";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

// Import StationTable
import StationTable from "../StationTable";

/**
 * StationSlidesConfig Component
 *
 * Handles the configuration of station slides with drag-and-drop reordering.
 * Now includes the station selection table within the component.
 *
 * @param {Object} props - Component props
 * @param {Array} props.selectedStations - Array of selected stations
 * @param {number|null} props.draggedStation - Index of currently dragged station
 * @param {Array} props.rows - All station rows data
 * @param {Array} props.filteredRows - Filtered station rows
 * @param {Object} props.filters - Current filter state
 * @param {string} props.search - Current search term
 * @param {Array} props.statusOptions - Status filter options
 * @param {Array} props.typeOptions - Type filter options
 * @param {Array} props.communityOptions - Community filter options
 * @param {Function} props.onDragStart - Drag start handler
 * @param {Function} props.onDragOver - Drag over handler
 * @param {Function} props.onDrop - Drop handler
 * @param {Function} props.onMoveUp - Move station up handler
 * @param {Function} props.onMoveDown - Move station down handler
 * @param {Function} props.onDisplayChange - Display change handler
 * @param {Function} props.onTimerChange - Timer change handler
 * @param {Function} props.onSearchChange - Search change handler
 * @param {Function} props.onFilterChange - Filter change handler
 *
 * @component
 */
export default function StationSlidesConfig({
  selectedStations,
  draggedStation,
  rows,
  filteredRows,
  filters,
  search,
  statusOptions,
  typeOptions,
  communityOptions,
  onDragStart,
  onDragOver,
  onDrop,
  onMoveUp,
  onMoveDown,
  onDisplayChange,
  onTimerChange,
  onLanguageChange,
  onSearchChange,
  onFilterChange,
}) {
  return (
    <Card
      elevation={8}
      sx={{
        border: "2px solid",
        borderColor:
          selectedStations.length > 0 ? "success.main" : "warning.main",
        borderRadius: 3,
        "&:hover": {
          boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <LocationOnIcon color="primary" sx={{ fontSize: 28 }} />
          <Typography variant="h6">Slide 3+: Station Slides</Typography>
          <Chip
            label={
              selectedStations.length > 0
                ? `${selectedStations.length} stations selected`
                : "Not Configured"
            }
            color={selectedStations.length > 0 ? "success" : "warning"}
            size="small"
          />
        </Stack>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select stations to include in the slideshow and arrange their display
          order. Each station will be shown as a separate slide. You can drag
          and drop to reorder. At least one language (English or Inuktitut) must
          be selected for each station. When both languages are selected, each
          slide runs for the same time one by one. Individual station timers can
          be customized in the table below.
        </Typography>

        {/* Selected Stations Order */}
        {selectedStations.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Display Order (Drag to reorder):
            </Typography>
            <Stack spacing={1}>
              {selectedStations.map((station, index) => {
                // Find the rowIndex in the full rows array for onDisplayChange
                const rowIndex = rows.findIndex((r) => r.id === station.id);
                // Determine chip color: Slide 1 (info), Slide 2 (primary), others (primary)
                let chipColor = "primary";
                if (index === -2)
                  chipColor = "info"; // Slide 1 (not shown here)
                else if (index === -1)
                  chipColor = "primary"; // Slide 2 (not shown here)
                else chipColor = "primary"; // Slide 3+ (these slides)
                return (
                  <Paper
                    key={station.id}
                    draggable
                    onDragStart={(e) => onDragStart(index, e)}
                    onDragOver={onDragOver}
                    onDrop={(e) => onDrop(index, e)}
                    sx={{
                      p: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      bgcolor: "grey.50",
                      border: "1px solid",
                      borderColor: "grey.300",
                      borderRadius: 2,
                      cursor: "grab",
                      boxShadow:
                        draggedStation === index
                          ? "0 4px 16px rgba(33,150,243,0.15)"
                          : undefined,
                      transition: "box-shadow 0.2s, background 0.2s",
                      opacity: draggedStation === index ? 0.5 : 1,
                      position: "relative",
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={2}
                      sx={{ flex: 1 }}
                    >
                      <DragIndicatorIcon color="primary" sx={{ mr: 1 }} />
                      <Chip
                        label={`Slide ${index + 3}`}
                        color={chipColor}
                        size="small"
                        variant="outlined"
                        sx={{ fontWeight: 700, fontSize: "0.95rem" }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, flex: 1, ml: 1 }}
                      >
                        {station.name}
                      </Typography>
                      {/* Language indicators */}
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        {station.languages?.english && (
                          <Chip
                            label="English"
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ fontSize: "0.7rem", height: 20 }}
                          />
                        )}
                        {station.languages?.inuktitut && (
                          <Chip
                            label="Inuktitut"
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ fontSize: "0.7rem", height: 20 }}
                          />
                        )}
                      </Stack>
                      <Tooltip
                        title={
                          station.timer
                            ? `Timer: ${station.timer} sec`
                            : "No timer set"
                        }
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.5}
                          sx={{ ml: 2, pr: 2 }}
                        >
                          <AccessTimeIcon
                            color="disabled"
                            sx={{ fontSize: 18 }}
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 500 }}
                          >
                            {station.timer
                              ? `${station.timer} sec`
                              : "No timer"}
                          </Typography>
                        </Stack>
                      </Tooltip>
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Tooltip title="Move up">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => onMoveUp(index)}
                            disabled={index === 0}
                            sx={{
                              bgcolor: "grey.100",
                              "&:hover": { bgcolor: "primary.50" },
                            }}
                          >
                            <KeyboardArrowUpIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Move down">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => onMoveDown(index)}
                            disabled={index === selectedStations.length - 1}
                            sx={{
                              bgcolor: "grey.100",
                              "&:hover": { bgcolor: "primary.50" },
                            }}
                          >
                            <KeyboardArrowDownIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Remove from slideshow">
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              if (rowIndex !== -1) {
                                onDisplayChange(rowIndex)({
                                  target: { checked: false },
                                });
                              }
                            }}
                            sx={{
                              bgcolor: "error.50",
                              "&:hover": { bgcolor: "error.100" },
                            }}
                          >
                            <CloseIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          </Box>
        )}

        {/* Station Selection Table - Now integrated within the component */}
        <Box sx={{ mt: 2 }}>
          <Typography
            variant="subtitle2"
            sx={{ mb: 2, fontWeight: 600, color: "text.secondary" }}
          >
            Station Selection Table:
          </Typography>
          <StationTable
            rows={rows}
            filteredRows={filteredRows}
            filters={filters}
            search={search}
            onDisplayChange={onDisplayChange}
            onTimerChange={onTimerChange}
            onSearchChange={onSearchChange}
            onFilterChange={onFilterChange}
            statusOptions={statusOptions}
            typeOptions={typeOptions}
            communityOptions={communityOptions}
            showMapButton={false}
            showSaveButton={false}
            mapConfig={null}
            lastModified={null}
            formatLastModified={null}
            onLanguageChange={onLanguageChange}
          />
        </Box>
      </CardContent>
    </Card>
  );
}
